import React from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import AssignmentActivity from './index'
import ScheduleAssignmentsTimeline from './ScheduleAssignmentsTimeline'

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`
const Page = styled.div`background: #FAFAFB; min-height: 100vh; animation: ${fadeIn} 0.3s ease-out;`
const HeaderBand = styled.div`background: #fff; border-bottom: 1px solid #EBECEE;`
const HeaderInner = styled.div`max-width: 1160px; margin: 0 auto; padding: 18px 28px; display: flex; align-items: flex-start; gap: 16px;`
const Accent = styled.div`width: 4px; align-self: stretch; min-height: 48px; border-radius: 2px; background: #3249D7; flex-shrink: 0;`
const Wrap = styled.div`max-width: 1160px; margin: 0 auto; padding: 22px 28px 48px;`
const BackLink = styled.button`
  background: none; border: none; color: #637487; font-weight: 600; font-size: 13px;
  cursor: pointer; padding: 0; margin-bottom: 10px; transition: color 0.2s;
  &:hover { color: #3249D7; }
`
const Title = styled.h2`font-size: 20px; font-weight: 700; color: #242E39; margin: 0; line-height: 1.3;`
const Sub = styled.div`font-size: 13px; color: #637487; margin-top: 4px;`

/**
 * Route page for the Assignment Activity view.
 *   /assessment/activity/job/:jobId        → one job (progress + failures + retry)
 *   /assessment/activity/schedule/:scheduleId → schedule timeline → drill into a job
 * Deep-linked from the popup, the summary email, and Manage Assessment.
 */
const AssignmentActivityPage = () => {
  const { jobId, scheduleId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  // Deterministic Back: return to the page we came from (set in nav state) so it's
  // always one click regardless of history depth. Email deep-links open a fresh
  // tab with no nav state and no app history, so history.back() is a dead end —
  // fall back to the reviews list instead of navigate(-1).
  const goBack = () => {
    const from = location.state && location.state.from
    navigate(from || '/assessment/reviews')
  }

  return (
    <Page>
      <HeaderBand>
        <HeaderInner>
          <Accent />
          <div style={{ flex: 1, minWidth: 0 }}>
            <BackLink onClick={goBack}>← Back</BackLink>
            <Title>{scheduleId ? 'Assignment activity — schedule' : 'Assignment activity'}</Title>
            <Sub>Live provisioning, assignment and notification status — updates in real time.</Sub>
          </div>
        </HeaderInner>
      </HeaderBand>
      <Wrap>
        {scheduleId ? (
          <ScheduleAssignmentsTimeline
            scheduleId={scheduleId}
            onOpenJob={(jid) => navigate(`/assessment/activity/job/${jid}`)}
          />
        ) : (
          <AssignmentActivity jobId={jobId} />
        )}
      </Wrap>
    </Page>
  )
}

export default AssignmentActivityPage
